import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Flower2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Petals } from "@/components/Petals";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [colors, setColors] = useState([]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setColors(data.colors || []);
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (err) { console.error("Payment status check failed, retrying", err); }
      attempts += 1;
      if (attempts >= 8) { setStatus("timeout"); return; }
      setTimeout(poll, 1800);
    };
    poll();
  }, [params]);

  return (
    <div className="garden-bg flex items-center justify-center min-h-[80vh] px-4">
      <Petals count={14} />
      <div className="card-glass rounded-3xl p-10 sm:p-14 text-center max-w-md border border-pink-500/25 relative z-10" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-pink-400 animate-spin mx-auto mb-5" />
            <p className="text-slate-300">Confirming your roses…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-600 to-rose-500 flex items-center justify-center mx-auto mb-6 pulse-glow">
              <Flower2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl text-pink-50 mb-3">Roses in bloom! 🌹</h1>
            <p className="text-slate-300">
              {colors.length} {colors.length === 1 ? "rose has" : "roses have"} been attached to the note.
            </p>
            <button
              data-testid="payment-success-continue"
              onClick={() => navigate("/")}
              className="mt-7 bg-gradient-to-r from-pink-600 to-rose-500 text-white font-medium px-7 py-3 rounded-full hover:scale-105 transition-all"
            >
              Back to the Garden
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-5" />
            <h1 className="font-serif text-2xl text-pink-50 mb-2">
              {status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-slate-400 text-sm">
              {status === "timeout" ? "Your payment is taking a moment. Roses will appear shortly." : "We couldn't confirm this payment."}
            </p>
            <button onClick={() => navigate("/")} className="mt-6 bg-white/10 border border-pink-500/25 text-pink-200 px-6 py-2.5 rounded-full hover:bg-white/15 transition-all">
              Back to the Garden
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="garden-bg flex items-center justify-center min-h-[80vh] px-4">
      <div className="card-glass rounded-3xl p-12 text-center max-w-md border border-pink-500/25" data-testid="payment-cancel">
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-5" />
        <h1 className="font-serif text-2xl text-pink-50 mb-2">Payment cancelled</h1>
        <p className="text-slate-400 text-sm">No roses were purchased. You can try again anytime.</p>
        <button onClick={() => navigate("/")} className="mt-6 bg-gradient-to-r from-pink-600 to-rose-500 text-white px-6 py-2.5 rounded-full hover:scale-105 transition-all">
          Back to the Garden
        </button>
      </div>
    </div>
  );
}
